<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue';

import { KaCheckbox } from '@/kasten-components/components/KaCheckbox';

import KaTableCell from './KaTableCell.vue';
import { injectRowContext, injectTableContext } from './model';

const { key } = injectRowContext();
const { isRowSelected, onToggleRow, onIncludeSelectableRow, isRowSelectable } =
  injectTableContext();

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    label?: string;
  }>(),
  {
    modelValue: undefined,
    label: 'Toggle row',
  },
);

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
  (e: 'update:modelValue', value: boolean): void;
}>();

const computedValue = computed<boolean>({
  get: () => isRowSelected(key.value),
  set: (value) => {
    onToggleRow(key.value, value);
    emit('update:modelValue', value);
  },
});

const handleClick = (e: MouseEvent) => {
  computedValue.value = !computedValue.value;
  emit('click', e);
};

const isSelectable = computed(() => isRowSelectable(key.value));

watch(
  () => props.modelValue,
  (valueOverride) => valueOverride !== undefined && onToggleRow(key.value, valueOverride),
  { immediate: true },
);

watch(
  key,
  (currentKey, prevKey) => {
    prevKey !== undefined && onIncludeSelectableRow(prevKey, false);
    onIncludeSelectableRow(currentKey, true);
  },
  { immediate: true },
);

onBeforeUnmount(() => onIncludeSelectableRow(key.value, false));
</script>
<template>
  <KaTableCell
    interactive
    class="checkboxCell w-0 cursor-not-allowed"
    :class="[
      { 'cursor-pointer [&&&>div>div>input]:cursor-pointer': isSelectable },
      '[&&&>div>div>input]:mt-0 [&&&>div>div>input]:checked:bg-[currentColor] [&&&>div>div]:h-auto',
    ]"
    data-testid="selectCell"
    @click.stop="handleClick"
  >
    <KaCheckbox
      :id="`select-row-${key}`"
      v-model="computedValue"
      :disabled="!isSelectable"
      hide-label
      :label="props.label"
      name="toggleRow"
      :class="{ 'cursor-not-allowed': !isSelectable }"
      @click.stop=""
    />
  </KaTableCell>
</template>
